import React, { type FunctionComponent } from 'react';
import ErrorBoundary from '@atlassian/jira-error-boundary/src/ErrorBoundary.tsx';
import Placeholder from '@atlassian/jira-placeholder/src/index.tsx';
import { lazy, LazyWait } from '@atlassian/react-loosely-lazy';
import { useInvitePeopleDrawer } from '../controllers/index.tsx';
import type InvitePeopleDrawerType from './content/index.tsx';

// eslint-disable-next-line jira/deprecations/no-rll-client-async-experiences
export const AsyncInvitePeopleDrawer = lazy<typeof InvitePeopleDrawerType>(
	() => import(/* webpackChunkName: "async-invite-people-drawer" */ './content'),
);

interface InvitePeopleDrawerInternalProps {
	disableNoInviteError?: boolean;
}

const InvitePeopleDrawerInternal: FunctionComponent<InvitePeopleDrawerInternalProps> = ({
	disableNoInviteError,
}) => {
	const [{ isOpen, inviteFlow, continueUrl }, { closeDrawer }] = useInvitePeopleDrawer();

	return (
		<LazyWait until={isOpen}>
			<Placeholder name="invite-people-drawer" fallback={null}>
				<AsyncInvitePeopleDrawer
					inviteFlow={inviteFlow}
					isOpen={isOpen}
					onClose={closeDrawer}
					continueUrl={continueUrl}
					displayModal
					disableNoInviteError={disableNoInviteError}
				/>
			</Placeholder>
		</LazyWait>
	);
};

InvitePeopleDrawerInternal.displayName = 'InvitePeopleDrawerInternal';

interface InvitePeopleDrawerWithErrorBoundaryProps {
	disableNoInviteError?: boolean;
}

const InvitePeopleDrawerWithErrorBoundary: FunctionComponent<
	InvitePeopleDrawerWithErrorBoundaryProps
> = ({ disableNoInviteError }) => (
	<ErrorBoundary id="invite-people-drawer" packageName="jiraInvitePeopleDrawer">
		<InvitePeopleDrawerInternal disableNoInviteError={disableNoInviteError} />
	</ErrorBoundary>
);

export default InvitePeopleDrawerWithErrorBoundary;
